import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const REASON_KEYS = [
  { icon: 'ri-store-line', statVal: '+18%', titleKey: 'price.r1.title', descKey: 'price.r1.desc', statLabelKey: 'price.r1.statLabel' },
  { icon: 'ri-leaf-line', statVal: '+22%', titleKey: 'price.r2.title', descKey: 'price.r2.desc', statLabelKey: 'price.r2.statLabel' },
  { icon: 'ri-drop-line', statVal: '+15%', titleKey: 'price.r3.title', descKey: 'price.r3.desc', statLabelKey: 'price.r3.statLabel' },
  { icon: 'ri-inbox-line', statVal: '-30%', titleKey: 'price.r4.title', descKey: 'price.r4.desc', statLabelKey: 'price.r4.statLabel' },
  { icon: 'ri-bar-chart-line', statVal: '+27%', titleKey: 'price.r5.title', descKey: 'price.r5.desc', statLabelKey: 'price.r5.statLabel' },
];

const useInView = (threshold = 0.15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
};

const PriceExplanation = () => {
  const { t } = useTranslation();
  const { ref, visible } = useInView();

  return (
    <section id="why-grows" className="py-20 md:py-28" style={{ background: 'linear-gradient(180deg, #0a1a0e 0%, #0d1117 60%, #111418 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-time-line text-green-400 text-sm"></i>
            <span className="text-green-400 text-xs font-semibold uppercase tracking-wide">{t('price.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl xl:text-5xl font-black text-white mb-4">
            {t('price.title_p1')} <span className="text-green-400">{t('price.title_hl')}</span>
          </h2>
          <p className="text-white/50 text-base max-w-2xl mx-auto">
            {t('price.subtitle')}
          </p>
        </div>

        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {REASON_KEYS.map((r, i) => (
            <div
              key={r.titleKey}
              className="group relative bg-white/5 border border-white/10 rounded-xl p-5 flex flex-col gap-4 hover:border-green-500/50 hover:-translate-y-1 transition-all duration-300 cursor-default"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.5s ease ${i * 0.1}s, transform 0.5s ease ${i * 0.1}s, border-color 0.3s, box-shadow 0.3s`,
              }}
            >
              <div className="absolute left-0 top-6 bottom-6 w-0.5 bg-green-500/30 group-hover:bg-green-400 rounded-full transition-colors duration-300"></div>
              <div className="w-10 h-10 flex items-center justify-center bg-green-500/10 rounded-lg">
                <i className={`${r.icon} text-green-400 text-xl`}></i>
              </div>
              <div>
                <h3 className="text-white font-bold text-sm mb-1.5">{t(r.titleKey)}</h3>
                <p className="text-white/50 text-xs leading-relaxed">{t(r.descKey)}</p>
              </div>
              <div className="mt-auto">
                <div className="text-green-400 font-black text-xl">{r.statVal}</div>
                <div className="text-white/40 text-xs">{t(r.statLabelKey)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom stat banner */}
        <div className="mt-12 bg-gradient-to-r from-green-900/30 via-green-800/20 to-green-900/30 border border-green-500/20 rounded-2xl p-8 text-center">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
            <div>
              <div className="text-green-400 font-black text-4xl">+34%</div>
              <div className="text-white/50 text-sm mt-1">{t('price.banner.avg')}</div>
            </div>
            <div className="hidden md:block w-px h-12 bg-white/10"></div>
            <div className="max-w-md text-left">
              <p className="text-white font-semibold text-base mb-1">
                {t('price.banner.desc')}{' '}
                <strong className="text-green-400">{t('price.banner.corn')}</strong>
                {t('price.banner.desc2')}
              </p>
              <p className="text-white/40 text-xs">{t('price.banner.disclaimer')}</p>
            </div>
            <div className="hidden md:block w-px h-12 bg-white/10"></div>
            <div>
              <div className="text-green-400 font-black text-4xl">100%</div>
              <div className="text-white/50 text-sm mt-1">{t('price.banner.years')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PriceExplanation;
